import type { Channel, Format, Post, PostStatus } from '../types';

const WEDNESDAY_18_NOVEMBER_2026 = 18;
const DEMO_CLOCK_ON_DEMO_TODAY_WHERE_PAUSES_START = '10:00';
const MISSED_POSTS_GO_OUT_LATE_ONLY_WITHIN_TWO_HOURS = 2 * 60;
const NOVEMBER_AS_ZERO_BASED_MONTH_INDEX = 10;

export const DEMO_TODAY = WEDNESDAY_18_NOVEMBER_2026;
export const DEMO_NOW = DEMO_CLOCK_ON_DEMO_TODAY_WHERE_PAUSES_START;
export const LATE_WINDOW_MINUTES = MISSED_POSTS_GO_OUT_LATE_ONLY_WITHIN_TWO_HOURS;
export const DEMO_YEAR = 2026;
export const DEMO_MONTH = NOVEMBER_AS_ZERO_BASED_MONTH_INDEX;

export type WorkspaceId = 'brzoza' | 'lipa' | 'nowa';
export interface Workspace {
  id: WorkspaceId;
  name: string;
  city: string;
  owner: string;
  ownerEmail: string;
  contributor: string;
  contributorEmail: string;
  channels: Channel[];
}

export const workspaces: Workspace[] = [
  {
    id: 'brzoza',
    name: 'Brzoza Coffee',
    city: 'Warszawa',
    owner: 'Anna Nowak',
    ownerEmail: 'anna@brzoza.example',
    contributor: 'Marek Zieliński',
    contributorEmail: 'marek@brzoza.example',
    channels: ['Facebook', 'Instagram', 'TikTok', 'LinkedIn'],
  },
  {
    id: 'lipa',
    name: 'Kawiarnia Lipa',
    city: 'Kraków',
    owner: 'Anna Nowak',
    ownerEmail: 'anna@lipa.example',
    contributor: 'Zofia Mazur',
    contributorEmail: 'zofia@lipa.example',
    channels: ['Facebook', 'Instagram'],
  },
  {
    id: 'nowa',
    name: 'New client (demo)',
    city: '',
    owner: 'Julia Wiśniewska',
    ownerEmail: 'julia@example.com',
    contributor: 'Piotr Wiśniewski',
    contributorEmail: 'piotr@example.com',
    channels: ['Instagram', 'Facebook', 'TikTok', 'LinkedIn'],
  },
];

const unsplashPhoto = (photoId: string) =>
  `https://images.unsplash.com/photo-${photoId}?w=700&q=80`;
const demoImages = {
  latte: unsplashPhoto('1570968915860-54d5c301fa9f'),
  cafe: unsplashPhoto('1442512595331-e89e73853f31'),
  beans: unsplashPhoto('1495474472287-4d71bcdd2085'),
  winter: unsplashPhoto('1517248135467-4c7edcad34c4'),
  dusk: unsplashPhoto('1554118811-1e0d58224f24'),
  art: unsplashPhoto('1501339847302-ac426a4a7cbb'),
  cake: unsplashPhoto('1488477181946-6428a0291777'),
  bar: unsplashPhoto('1453614512568-c4024d13c247'),
  tea: unsplashPhoto('1544787219-7f47ccb76574'),
  pierogi: unsplashPhoto('1600626333392-fe7e3d1a7e3b'),
  jazz: unsplashPhoto('1511192336575-5a79af67a629'),
  buns: unsplashPhoto('1509365465985-25d11c17e812'),
  bread: unsplashPhoto('1509440159596-0249088772ff'),
  table: unsplashPhoto('1521017432531-fbd92d768814'),
};

type PostSeedRow = [
  day: number,
  channel: Channel,
  time: string,
  title: string,
  format: Format,
  status: PostStatus,
  campaign: string,
  language?: 'pl' | 'en',
];
const accountNameByWorkspaceAndChannel: Record<WorkspaceId, Record<Channel, string>> = {
  brzoza: {
    Facebook: 'Brzoza Coffee',
    Instagram: '@brzozacoffee',
    TikTok: '@brzozacoffee',
    LinkedIn: 'Brzoza Coffee',
  },
  lipa: {
    Facebook: 'Kawiarnia Lipa',
    Instagram: '@kawiarnialipa',
    TikTok: '@kawiarnialipa',
    LinkedIn: 'Kawiarnia Lipa',
  },
  nowa: { Facebook: '', Instagram: '', TikTok: '', LinkedIn: '' },
};

const captions: Record<string, string> = {
  'Slow mornings, good coffee':
    'Spokojny poranek smakuje lepiej z dobrą kawą. Usiądź przy oknie, my zajmiemy się resztą ☕ #BrzozaCoffee',
  'Meet the roaster':
    'Meet Kuba, who roasts every batch of our beans here in Warsaw. Small batches, every Tuesday. #BrzozaCoffee',
  'A little winter ritual':
    'Zimowy rytuał: kubek ciepłej kawy, kawałek ciasta i chwila dla siebie. Wpadnij do nas! #ZimaWBrzozie',
  'Behind the bar':
    'Za barem: tak powstaje Twoje flat white. Zobacz, ile uwagi wkładamy w każdą filiżankę ☕ #BrzozaCoffee',
  'Gingerbread latte is here':
    'Piernikowe latte wróciło. Ziarna palimy tu, w Warszawie, powoli i z uwagą ☕ Wpadnij do nas, czekamy przy oknie. #BrzozaCoffee #ZimaWBrzozie',
  'Our beans, your morning':
    'Nasze ziarna, Twój poranek. Kup paczkę na wynos i zaparz Brzozę w domu 🌿 #BrzozaCoffee',
  'Our roastery in Warsaw':
    'Inside our Warsaw roastery: how we choose, roast and taste every single-origin coffee we serve.',
  'Cosy season starts now':
    'Sezon na przytulność czas zacząć. Koce, świece i gorąca czekolada już czekają 🍂 #ZimaWBrzozie',
  'A weekend at Brzoza':
    'Weekend w Brzozie: śniadania do 13:00, świeże ciasto i najlepsze miejsca przy oknie. #BrzozaCoffee',
  'Sunday cake club':
    'Niedzielny klub ciasta: co tydzień nowy wypiek. W tę niedzielę sernik z żurawiną 🍰',
  'New winter menu':
    'Nowe menu zimowe już jest! Piernikowe latte, chai z kardamonem i szarlotka na ciepło. #ZimaWBrzozie',
  'Team story: Ola':
    'Meet Ola, our head barista. Ask her about the best filter coffee for a rainy afternoon.',
  'Latte art in 15 seconds':
    'Latte art w 15 sekund. Potrafisz szybciej? ☕ #latteart #BrzozaCoffee',
  'Window seats':
    'Najlepsze miejsca w mieście? Te przy naszym oknie. Zarezerwuj stolik na sobotę 🌿',
  'The perfect pairing':
    'Idealna para: espresso i domowe ciasto marchewkowe. Spróbuj w tym tygodniu! #BrzozaCoffee',
  'Weekend tasting': 'W sobotę degustacja kaw z Etiopii i Kolumbii, 11:00–13:00. Zapraszamy!',
  'Linden tea season':
    'Sezon na herbatę lipową! Parzymy ją z miodem z Małopolski 🍯 #KawiarniaLipa',
  'Pierogi Thursday':
    'Czwartek = pierogi. Dziś z kapustą i grzybami, podane z masłem i cebulką. #KawiarniaLipa',
  'Live jazz on Friday':
    'W piątek o 19:00 gra Trio Kazimierz. Wstęp wolny, rezerwacje mile widziane 🎷',
  'Breakfast until noon':
    'Śniadania serwujemy do 12:00, także w weekend. Jajecznica z kurkami czeka!',
  'Meet our baker Zosia':
    'Poznajcie Zosię, która piecze dla nas od piątej rano. Jej drożdżówki znikają do dziesiątej!',
  'Cinnamon buns are back':
    'Cynamonki wróciły! Ciepłe, pachnące i tylko do wyczerpania zapasów. #KawiarniaLipa',
  'A table by the window':
    'Stolik przy oknie, widok na Kazimierz i filiżanka kawy. Tak wygląda listopad w Lipie.',
};

const imageFor: Record<string, string> = {
  'Slow mornings, good coffee': demoImages.cafe,
  'Meet the roaster': demoImages.beans,
  'A little winter ritual': demoImages.cake,
  'Behind the bar': demoImages.bar,
  'Gingerbread latte is here': demoImages.latte,
  'Our beans, your morning': demoImages.beans,
  'Our roastery in Warsaw': demoImages.beans,
  'Cosy season starts now': demoImages.winter,
  'A weekend at Brzoza': demoImages.table,
  'Sunday cake club': demoImages.cake,
  'New winter menu': demoImages.winter,
  'Team story: Ola': demoImages.art,
  'Latte art in 15 seconds': demoImages.art,
  'Window seats': demoImages.dusk,
  'The perfect pairing': demoImages.cake,
  'Weekend tasting': demoImages.beans,
  'Linden tea season': demoImages.tea,
  'Pierogi Thursday': demoImages.pierogi,
  'Live jazz on Friday': demoImages.jazz,
  'Breakfast until noon': demoImages.bread,
  'Meet our baker Zosia': demoImages.bread,
  'Cinnamon buns are back': demoImages.buns,
  'A table by the window': demoImages.dusk,
};

const brzozaRows: PostSeedRow[] = [
  [16, 'Instagram', '09:00', 'Slow mornings, good coffee', 'Photo', 'Published', 'Winter menu'],
  [16, 'Facebook', '13:30', 'Slow mornings, good coffee', 'Photo', 'Published', 'Winter menu'],
  [16, 'LinkedIn', '17:15', 'Meet the roaster', 'Photo', 'Published', 'Always-on', 'en'],
  [17, 'Instagram', '13:30', 'A little winter ritual', 'Carousel', 'Published', 'Winter menu'],
  [17, 'Facebook', '13:30', 'A little winter ritual', 'Photo', 'Published', 'Winter menu'],
  [17, 'LinkedIn', '09:00', 'Our roastery in Warsaw', 'PDF', 'Published', 'Always-on', 'en'],
  [18, 'Instagram', '09:00', 'Behind the bar', 'Photo', 'Published', 'Always-on'],
  [18, 'Facebook', '17:15', 'Behind the bar', 'Photo', 'Scheduled', 'Always-on'],
  [
    18,
    'TikTok',
    '17:15',
    'Slow mornings, good coffee',
    'Reel',
    'Approved · Stopped',
    'Winter menu',
  ],
  [
    19,
    'Instagram',
    '09:00',
    'Gingerbread latte is here',
    'Reel',
    'Needs your review',
    'Winter menu',
  ],
  [
    19,
    'Facebook',
    '09:00',
    'Gingerbread latte is here',
    'Photo',
    'Needs your review',
    'Winter menu',
  ],
  [19, 'TikTok', '12:00', 'Gingerbread latte is here', 'Reel', 'Needs your review', 'Winter menu'],
  [
    20,
    'Instagram',
    '13:30',
    'Our beans, your morning',
    'Carousel',
    'Needs your review',
    'Always-on',
  ],
  [20, 'Facebook', '13:30', 'Our beans, your morning', 'Photo', 'Needs your review', 'Always-on'],
  [20, 'LinkedIn', '09:00', 'Team story: Ola', 'Photo', 'Needs your review', 'Always-on', 'en'],
  [21, 'Instagram', '10:00', 'A weekend at Brzoza', 'Reel', 'AMY is working', 'Winter menu'],
  [21, 'Facebook', '10:00', 'A weekend at Brzoza', 'Photo', 'Scheduled', 'Winter menu'],
  [22, 'Instagram', '11:00', 'Sunday cake club', 'Photo', 'Planned', 'Always-on'],
  [22, 'Facebook', '17:15', 'Cosy season starts now', 'Photo', 'Scheduled', 'Winter menu'],
  [23, 'Instagram', '09:00', 'New winter menu', 'Carousel', 'Scheduled', 'Winter menu'],
  [23, 'Facebook', '09:00', 'New winter menu', 'Photo', 'Scheduled', 'Winter menu'],
  [24, 'TikTok', '17:15', 'Latte art in 15 seconds', 'Reel', 'Needs your input', 'Winter menu'],
  [25, 'Instagram', '13:30', 'Window seats', 'Photo', 'Planned', 'Always-on'],
  [26, 'Instagram', '13:30', 'The perfect pairing', 'Photo', 'Planned', 'Winter menu'],
  [26, 'Facebook', '13:30', 'The perfect pairing', 'Photo', 'Planned', 'Winter menu'],
  [27, 'Instagram', '09:00', 'Weekend tasting', 'Photo', 'Planned', 'Winter menu'],
  [29, 'Facebook', '11:00', 'Window seats', 'Photo', 'Intentionally empty', 'Always-on'],
];

const postWhoseTimeHasPassedBeforeApproval: PostSeedRow = [
  DEMO_TODAY,
  'Facebook',
  '09:00',
  'Breakfast until noon',
  'Photo',
  'Needs your review',
  'Every day',
];

const lipaRows: PostSeedRow[] = [
  [16, 'Instagram', '08:30', 'Breakfast until noon', 'Photo', 'Published', 'Every day'],
  [17, 'Facebook', '12:00', 'Linden tea season', 'Photo', 'Published', 'Autumn at Lipa'],
  [18, 'Instagram', '12:00', 'Linden tea season', 'Reel', 'Scheduled', 'Autumn at Lipa'],
  [19, 'Facebook', '11:00', 'Pierogi Thursday', 'Photo', 'Needs your review', 'Every day'],
  [19, 'Instagram', '11:00', 'Pierogi Thursday', 'Carousel', 'Needs your review', 'Every day'],
  [20, 'Facebook', '16:00', 'Live jazz on Friday', 'Photo', 'Needs your review', 'Events'],
  [20, 'Instagram', '16:00', 'Live jazz on Friday', 'Reel', 'Needs your review', 'Events'],
  [21, 'Instagram', '09:00', 'Meet our baker Zosia', 'Photo', 'Needs your review', 'Every day'],
  [
    22,
    'Facebook',
    '10:00',
    'Cinnamon buns are back',
    'Photo',
    'Needs your review',
    'Autumn at Lipa',
  ],
  [24, 'Instagram', '12:00', 'A table by the window', 'Photo', 'Planned', 'Autumn at Lipa'],
  [26, 'Facebook', '11:00', 'Pierogi Thursday', 'Photo', 'Planned', 'Every day'],
  postWhoseTimeHasPassedBeforeApproval,
];

function buildPosts(workspaceId: WorkspaceId, rows: PostSeedRow[], prefix: string): Post[] {
  return rows.map(([day, channel, time, title, format, status, campaign, language], index) => {
    const id = `${prefix}${index + 1}`;
    const published = status === 'Published';
    const revised = status === 'Needs your review' && index % 2 === 0;
    const post: Post = {
      id,
      workspaceId,
      title,
      channel,
      account: accountNameByWorkspaceAndChannel[workspaceId][channel],
      status,
      day,
      time,
      format,
      groupId: `${prefix}${title}-${day}`,
      image: imageFor[title],
      caption: captions[title],
      language: language ?? 'pl',
      campaign,
      brief: `${title} · ${campaign}`,
      revision: revised ? 2 : 1,
      changeNote: revised ? 'Brightened the photo and made the opening line warmer.' : undefined,
      previousCaption: revised
        ? captions[title].replace('Wpadnij do nas', 'Zapraszamy')
        : undefined,
      results: published
        ? [
            {
              account: accountNameByWorkspaceAndChannel[workspaceId][channel],
              succeeded: true,
              at: time,
            },
          ]
        : undefined,
      missing:
        status === 'Needs your input'
          ? 'Three short video clips (5–10 s) of latte art being poured.'
          : undefined,
      history: [{ at: `${Math.max(day - 4, 10)} Nov`, by: 'AMY', text: 'Draft created' }],
      comments: [],
    };
    return post;
  });
}

const brzozaPosts = buildPosts('brzoza', brzozaRows, 'brz-');
const linkedInPostSentToCompanyPageAndUnconnectedPersonalProfile = brzozaPosts.find(
  (post) => post.title === 'Our roastery in Warsaw'
);
const instagramPostAwaitingCheckBeforeRetrying = brzozaPosts.find(
  (post) => post.title === 'A little winter ritual' && post.channel === 'Instagram'
);
if (instagramPostAwaitingCheckBeforeRetrying)
  instagramPostAwaitingCheckBeforeRetrying.results = [
    {
      account: '@brzozacoffee',
      succeeded: false,
      uncertain: true,
      reason:
        'Instagram did not confirm in time. AMY checks whether it went out before trying again.',
    },
  ];
if (linkedInPostSentToCompanyPageAndUnconnectedPersonalProfile)
  linkedInPostSentToCompanyPageAndUnconnectedPersonalProfile.results = [
    { account: 'Brzoza Coffee (company page)', succeeded: true, at: '09:00' },
    {
      account: 'Marek Zieliński (personal)',
      succeeded: false,
      reason: 'Marek has not connected his LinkedIn account yet.',
    },
  ];
const scheduledPostStoppedByAnna = brzozaPosts.find((post) => post.status === 'Approved · Stopped');
if (scheduledPostStoppedByAnna)
  scheduledPostStoppedByAnna.history.push({
    at: '17 Nov',
    by: 'Anna Nowak',
    text: 'Stopped before publishing',
  });

export const seedPosts: Post[] = [...brzozaPosts, ...buildPosts('lipa', lipaRows, 'lip-')];

export type Health = 'Connected' | 'Needs reconnecting' | 'Not connected';
export interface Account {
  id: string;
  workspaceId: WorkspaceId;
  network: Channel;
  name: string;
  type: string;
  health: Health;
  owner: string;
  connectedAt?: string;
  expires?: string;
}
export const seedAccounts: Account[] = [
  {
    id: 'brz-fb',
    workspaceId: 'brzoza',
    network: 'Facebook',
    name: 'Brzoza Coffee',
    type: 'Facebook Page',
    health: 'Connected',
    owner: 'Anna Nowak',
  },
  {
    id: 'brz-ig',
    workspaceId: 'brzoza',
    network: 'Instagram',
    name: '@brzozacoffee',
    type: 'Business account',
    health: 'Connected',
    owner: 'Anna Nowak',
  },
  {
    id: 'brz-tt',
    workspaceId: 'brzoza',
    network: 'TikTok',
    name: '@brzozacoffee',
    type: 'TikTok',
    health: 'Needs reconnecting',
    owner: 'Anna Nowak',
    connectedAt: '2 Sep',
    expires: '16 Nov',
  },
  {
    id: 'brz-li',
    workspaceId: 'brzoza',
    network: 'LinkedIn',
    name: 'Brzoza Coffee',
    type: 'LinkedIn company page',
    health: 'Connected',
    owner: 'Anna Nowak',
  },
  {
    id: 'brz-lip',
    workspaceId: 'brzoza',
    network: 'LinkedIn',
    name: 'Marek Zieliński',
    type: 'LinkedIn personal',
    health: 'Not connected',
    owner: 'Marek Zieliński',
  },
  {
    id: 'lip-fb',
    workspaceId: 'lipa',
    network: 'Facebook',
    name: 'Kawiarnia Lipa',
    type: 'Facebook Page',
    health: 'Connected',
    owner: 'Anna Nowak',
  },
  {
    id: 'lip-ig',
    workspaceId: 'lipa',
    network: 'Instagram',
    name: '@kawiarnialipa',
    type: 'Business account',
    health: 'Connected',
    owner: 'Anna Nowak',
  },
];

const withoutAccountKindSuffix = (accountLabel: string) =>
  accountLabel.replace(/ \((company page|personal)\)$/, '');

export const postTargets = (post: { account: string; results?: { account: string }[] }) => [
  ...new Set([
    post.account,
    ...(post.results ?? []).map((result) => withoutAccountKindSuffix(result.account)),
  ]),
];
export const onAccount = (
  post: { channel: Channel; account: string; results?: { account: string }[] },
  account: Account
) => post.channel === account.network && postTargets(post).includes(account.name);

type NovemberUtcOffsetMinutesWithoutDaylightSaving = Record<string, number>;

export const UTC_OFFSET_MINUTES_IN_NOVEMBER: NovemberUtcOffsetMinutesWithoutDaylightSaving = {
  'Europe/Warsaw': 60,
  'Europe/London': 0,
  'Europe/Helsinki': 120,
  'America/New_York': -300,
};
const WARSAW_OFFSET_MINUTES = 60;
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

export function shiftTime(
  time: string,
  from: string,
  to: string
): { time: string; dayShift: number } {
  const [hours, minutes] = time.split(':').map(Number);
  const fromOffset = UTC_OFFSET_MINUTES_IN_NOVEMBER[from] ?? WARSAW_OFFSET_MINUTES;
  const toOffset = UTC_OFFSET_MINUTES_IN_NOVEMBER[to] ?? WARSAW_OFFSET_MINUTES;
  const shiftedMinutesSinceMidnight = hours * MINUTES_PER_HOUR + minutes - fromOffset + toOffset;
  const dayShift = Math.floor(shiftedMinutesSinceMidnight / MINUTES_PER_DAY);
  const minutesIntoShiftedDay =
    ((shiftedMinutesSinceMidnight % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const shiftedHours = String(Math.floor(minutesIntoShiftedDay / MINUTES_PER_HOUR)).padStart(
    2,
    '0'
  );
  const shiftedMinutes = String(minutesIntoShiftedDay % MINUTES_PER_HOUR).padStart(2, '0');
  return {
    time: `${shiftedHours}:${shiftedMinutes}`,
    dayShift,
  };
}
export const accountProblem = (account: Account) => {
  const isOptionalPersonalProfile = /personal/i.test(account.type);
  if (isOptionalPersonalProfile) return account.health === 'Needs reconnecting';
  return account.health !== 'Connected';
};

export interface Asset {
  id: string;
  workspaceId: WorkspaceId;
  name: string;
  kind: 'Photo' | 'Video' | 'Audio' | 'Document';
  dur?: string;
  image: string;
  badges: ('Linked' | 'Used in posts' | 'Preparing media')[];
  file?: string;
  size?: string;
  tags?: string[];
  favourite?: boolean;
  archived?: boolean;
  source?: 'Uploaded' | 'Drive';
  added?: string;
  preparingUntil?: number;
}
export const seedAssets: Asset[] = [
  {
    id: 'a1',
    workspaceId: 'brzoza',
    name: 'Gingerbread latte',
    tags: ['winter menu', 'drinks'],
    kind: 'Photo',
    image: demoImages.latte,
    badges: ['Used in posts'],
  },
  {
    id: 'a2',
    workspaceId: 'brzoza',
    name: 'Winter counter',
    tags: ['interior'],
    kind: 'Photo',
    image: demoImages.cafe,
    badges: ['Linked', 'Used in posts'],
  },
  {
    id: 'a3',
    workspaceId: 'brzoza',
    name: 'Brzoza beans',
    tags: ['beans', 'product'],
    kind: 'Photo',
    image: demoImages.beans,
    badges: ['Used in posts'],
  },
  {
    id: 'a4',
    workspaceId: 'brzoza',
    name: 'Weekend reel',
    tags: ['weekend'],
    kind: 'Video',
    dur: '0:24',
    image: demoImages.winter,
    badges: ['Linked', 'Preparing media'],
  },
  {
    id: 'a5',
    workspaceId: 'brzoza',
    name: 'Cafe at dusk',
    tags: ['interior', 'evening'],
    kind: 'Photo',
    image: demoImages.dusk,
    badges: [],
  },
  {
    id: 'a6',
    workspaceId: 'brzoza',
    name: 'Latte art',
    tags: ['drinks', 'barista'],
    kind: 'Video',
    dur: '0:18',
    image: demoImages.art,
    badges: ['Linked'],
  },
  {
    id: 'a7',
    workspaceId: 'brzoza',
    name: 'Morning playlist',
    tags: ['music'],
    kind: 'Audio',
    dur: '0:30',
    image: demoImages.table,
    badges: [],
  },
  {
    id: 'b1',
    workspaceId: 'lipa',
    name: 'Linden tea',
    tags: ['drinks'],
    kind: 'Photo',
    image: demoImages.tea,
    badges: ['Used in posts'],
  },
  {
    id: 'b2',
    workspaceId: 'lipa',
    name: 'Pierogi plate',
    tags: ['food'],
    kind: 'Photo',
    image: demoImages.pierogi,
    badges: ['Linked', 'Used in posts'],
  },
  {
    id: 'b3',
    workspaceId: 'lipa',
    name: 'Jazz evening',
    tags: ['events'],
    kind: 'Video',
    dur: '0:32',
    image: demoImages.jazz,
    badges: ['Linked'],
  },
  {
    id: 'b4',
    workspaceId: 'lipa',
    name: 'Cinnamon buns',
    tags: ['bakery'],
    kind: 'Photo',
    image: demoImages.buns,
    badges: ['Used in posts'],
  },
];

export type UploadState =
  | 'uploading'
  | 'paused'
  | 'preparing'
  | 'done'
  | 'failed'
  | 'unsupported'
  | 'duplicate'
  | 'cancelled';
export interface Upload {
  id: string;
  workspaceId: WorkspaceId;
  name: string;
  kind: Asset['kind'];
  sizeMb: number;
  progress: number;
  state: UploadState;
  note?: string;
  noteVars?: Record<string, string>;
  src?: string;
  forPost?: string;
  readyAt?: number;
}
export const seedUploads: Upload[] = [
  {
    id: 'u1',
    workspaceId: 'brzoza',
    name: 'IMG_2103.HEIC',
    kind: 'Photo',
    sizeMb: 4.2,
    progress: 100,
    state: 'preparing',
  },
  {
    id: 'u2',
    workspaceId: 'brzoza',
    name: 'winter_reel.mov',
    kind: 'Video',
    sizeMb: 1840,
    progress: 42,
    state: 'paused',
    note: 'Paused when the phone went to sleep. Resume when you are on Wi-Fi.',
  },
  {
    id: 'u3',
    workspaceId: 'brzoza',
    name: 'latte_pour_02.mov',
    kind: 'Video',
    sizeMb: 612,
    progress: 71,
    state: 'failed',
    note: 'The connection dropped at {p}%. Retry continues from where it stopped.',
    noteVars: { p: '71' },
  },
  {
    id: 'u4',
    workspaceId: 'brzoza',
    name: 'menu-design.psd',
    kind: 'Document',
    sizeMb: 88,
    progress: 0,
    state: 'unsupported',
    note: 'Photoshop files cannot be used in posts. Export a JPG or PNG instead.',
  },
  {
    id: 'u5',
    workspaceId: 'brzoza',
    name: 'IMG_1648.HEIC',
    kind: 'Photo',
    sizeMb: 3.1,
    progress: 100,
    state: 'duplicate',
    note: 'Already in the Material bank as “{name}”.',
    noteVars: { name: 'Winter counter' },
  },
];

export interface DriveLink {
  workspaceId: WorkspaceId;
  connected: boolean;
  account: string;
  folder: string;
  access: 'ok' | 'lost';
  lastChecked: string;
  fileCount: number;
}
export const seedDrive: DriveLink[] = [
  {
    workspaceId: 'brzoza',
    connected: true,
    account: 'anna@brzoza.example',
    folder: 'Brzoza Marketing',
    access: 'ok',
    lastChecked: '09:55',
    fileCount: 512,
  },
  {
    workspaceId: 'lipa',
    connected: false,
    account: '',
    folder: '',
    access: 'ok',
    lastChecked: '',
    fileCount: 0,
  },
  {
    workspaceId: 'nowa',
    connected: false,
    account: '',
    folder: '',
    access: 'ok',
    lastChecked: '',
    fileCount: 0,
  },
];

export interface Message {
  attachments?: { name: string; src?: string }[];
  id: string;
  from: 'amy' | 'user' | 'system';
  by?: string;
  at: string;
  text: string;
  variables?: Record<string, string>;
  showAsWritten?: boolean;
  postId?: string;
  task?: { title: string; showAsWritten?: boolean; status: 'Queued' | 'Working' | 'Ready' };
  taskId?: string;
}
export interface Conversation {
  id: string;
  workspaceId: WorkspaceId;
  title: string;
  showAsWritten?: boolean;
  scope: 'general' | 'campaign' | 'post';
  postId?: string;
  unread: number;
  messages: Message[];
}
export const seedConversations: Conversation[] = [
  {
    id: 'brz-c1',
    workspaceId: 'brzoza',
    title: 'General',
    scope: 'general',
    unread: 0,
    messages: [
      {
        id: 'm1',
        from: 'amy',
        at: '09:12',
        text: 'Good morning, Anna! Six posts are ready for your review, including the gingerbread latte for Thursday.',
      },
      {
        id: 'm2',
        from: 'user',
        by: 'Anna Nowak',
        at: '09:15',
        showAsWritten: true,
        text: 'Can you make a post about our new gingerbread latte for Thursday morning?',
      },
      {
        id: 'm3',
        from: 'amy',
        at: '09:16',
        text: 'Done. The Instagram Reel is ready for Thursday 19 November at 9:00, with Facebook and TikTok versions.',
        postId: 'brz-10',
      },
    ],
  },
  {
    id: 'brz-c2',
    workspaceId: 'brzoza',
    title: 'Winter menu campaign',
    scope: 'campaign',
    unread: 2,
    messages: [
      {
        id: 'm4',
        from: 'amy',
        at: 'Yesterday',
        text: 'The winter menu plan runs from 16 to 29 November. Five posts are waiting for your review.',
      },
      {
        id: 'm5',
        from: 'user',
        by: 'Marek Zieliński',
        at: 'Yesterday',
        showAsWritten: true,
        text: 'Can we add one about the cinnamon buns?',
      },
      {
        id: 'm6',
        from: 'amy',
        at: 'Yesterday',
        text: 'Yes. I will add it on Friday 27 November when I prepare the next batch.',
        task: { title: 'Add a cinnamon buns post to the winter menu', status: 'Queued' },
        taskId: 't-buns',
      },
    ],
  },
  {
    id: 'brz-c3',
    workspaceId: 'brzoza',
    title: 'Gingerbread latte',
    scope: 'post',
    postId: 'brz-10',
    unread: 1,
    messages: [
      {
        id: 'm7',
        from: 'amy',
        at: '08:40',
        text: 'I brightened the photo and made the opening line warmer, as you asked.',
        postId: 'brz-10',
      },
    ],
  },
  {
    id: 'lip-c1',
    workspaceId: 'lipa',
    title: 'General',
    scope: 'general',
    unread: 1,
    messages: [
      {
        id: 'm8',
        from: 'amy',
        at: '08:55',
        text: 'Dzień dobry! Pierogi Thursday and the Friday jazz evening are ready for your review.',
        postId: 'lip-4',
      },
    ],
  },
  {
    id: 'nowa-c1',
    workspaceId: 'nowa',
    title: 'General',
    scope: 'general',
    unread: 0,
    messages: [
      {
        id: 'nw1',
        from: 'amy',
        at: '10:00',
        text: 'Welcome to AMY! A few short questions and I will show you three sample posts in your style.',
      },
    ],
  },
];

export type CampaignGoal = 'product' | 'season' | 'event' | 'awareness' | 'always-on';
export type CampaignStatus =
  | 'Draft'
  | 'Planning'
  | 'Plan ready'
  | 'Ideas ready'
  | 'Active'
  | 'Needs your input'
  | 'Paused'
  | 'Finished';
export interface Idea {
  id: string;
  title: string;
  angle: string;
  channel: Channel;
  format: Format;
  status: 'Suggested' | 'In calendar' | 'Declined';
  postId?: string;
}
export interface PlanSlot {
  day: number;
  channel: Channel;
  format: Format;
  title: string;
  postId?: string;
}
export interface Campaign {
  id: string;
  workspaceId: WorkspaceId;
  name: string;
  key: string;
  goal: CampaignGoal;
  status: CampaignStatus;
  start?: number;
  end?: number;
  channels: Channel[];
  perWeek: number;
  brief: string;
  materials: string[];
  ideas: Idea[];
  conversationId?: string;
  question?: string;
  createdBy: string;
  owner?: string;
  audience?: string;
  focus?: string;
  languages?: ('pl' | 'en')[];
  accounts?: string[];
  template?: string;
  plan?: PlanSlot[];
  batchSize?: number;
  nextBatch?: number;
  samplesApproved?: boolean;
  creationPaused?: boolean;
  publishingPaused?: boolean;
}
export interface Task {
  id: string;
  workspaceId: WorkspaceId;
  title: string;
  variables?: Record<string, string>;
  showAsWritten?: boolean;
  status: 'Queued' | 'Working' | 'Ready' | 'Failed' | 'Cancelled' | 'Waiting' | 'With agency';
  kind: 'post' | 'ideas' | 'note' | 'plan';
  by: string;
  at: string;
  conversationId?: string;
  campaignId?: string;
  plannedFor?: number;
  startAt?: number;
  readyAt?: number;
  reason?: string;
  question?: string;
  answer?: string;
  materials?: string[];
  channels?: Channel[];
  day?: number;
  interpretation?: { text: string; variables?: Record<string, string> };
  nextStep?: string;
  allowAfter?: boolean;
  skipLate?: boolean;
  questionVars?: Record<string, string>;
  choices?: { label: string; value: string }[];
  result?: {
    text: string;
    variables?: Record<string, string>;
    postIds?: string[];
    ideaCount?: number;
  };
}
export interface Template {
  id: string;
  name: string;
  goal: CampaignGoal;
  description: string;
  inputs: string[];
  formats: Format[];
  example: { title: string; caption: string };
  by: 'AMY' | 'Hakusana';
}
export const seedTemplates: Template[] = [
  {
    id: 'tpl-product',
    name: 'Product launch',
    goal: 'product',
    description: 'Tease, launch day, how it is made, last chance.',
    inputs: ['Product name', 'Launch date', 'One photo of the product'],
    formats: ['Reel', 'Photo', 'Carousel'],
    example: {
      title: 'First sip: hot chocolate',
      caption:
        'It is here. Thick, dark and made with real cocoa. Come and try the first cup by the window.',
    },
    by: 'AMY',
  },
  {
    id: 'tpl-event',
    name: 'Event',
    goal: 'event',
    description: 'Save the date, what to expect, same-day reminder, thank you.',
    inputs: ['Event name', 'Date and time', 'Photo from a past event'],
    formats: ['Photo', 'Carousel', 'Story'],
    example: {
      title: 'Live jazz on Friday',
      caption: 'Friday from 19:00: the Lipa Trio plays by the window. Free entry, book a table.',
    },
    by: 'AMY',
  },
  {
    id: 'tpl-team',
    name: 'Team story',
    goal: 'awareness',
    description: 'One person, one favourite drink, one short story.',
    inputs: ['Person and role', 'A photo of them', 'Their favourite drink'],
    formats: ['Photo', 'Carousel'],
    example: {
      title: 'Meet Ola, our head barista',
      caption:
        'Ola has poured over 20,000 flat whites. Her favourite? A cortado at 7 am, before the doors open.',
    },
    by: 'AMY',
  },
  {
    id: 'tpl-announce',
    name: 'Announcement',
    goal: 'event',
    description: 'Clear news: opening hours, closures, price changes.',
    inputs: ['What changes', 'From when', 'Who it affects'],
    formats: ['Photo', 'Story'],
    example: {
      title: 'Holiday opening hours',
      caption: 'We are closed 24–26 December and open 10–14 on New Year’s Eve. Happy holidays!',
    },
    by: 'AMY',
  },
];
export const seedCampaigns: Campaign[] = [
  {
    id: 'cmp-winter',
    workspaceId: 'brzoza',
    name: 'Winter menu campaign',
    key: 'Winter menu',
    goal: 'season',
    status: 'Active',
    start: 16,
    end: 29,
    channels: ['Instagram', 'Facebook', 'TikTok'],
    perWeek: 6,
    brief:
      'Introduce the winter menu: gingerbread latte, apple cake and the new hot chocolate. Warm, cosy, real guests. Invite people to the window seats.',
    materials: ['Gingerbread latte', 'Winter counter', 'Weekend reel'],
    conversationId: 'brz-c2',
    createdBy: 'Anna Nowak',
    owner: 'Anna Nowak',
    audience: 'Neighbours and office workers nearby, 25–45',
    focus: 'Gingerbread latte, apple cake, hot chocolate',
    languages: ['pl'],
    accounts: ['brz-ig', 'brz-fb', 'brz-tt'],
    batchSize: 3,
    nextBatch: 25,
    samplesApproved: true,
    ideas: [
      {
        id: 'i-w1',
        title: 'Hot chocolate, slowly',
        angle: 'Close-up pour, steam in the window light. Shows the new drink.',
        channel: 'Instagram',
        format: 'Reel',
        status: 'Suggested',
      },
      {
        id: 'i-w2',
        title: 'Apple cake from Grandma’s recipe',
        angle: 'The story behind the cake, told by the baker.',
        channel: 'Facebook',
        format: 'Photo',
        status: 'Suggested',
      },
      {
        id: 'i-w3',
        title: 'Guess the spice',
        angle: 'Playful quiz about what goes into the gingerbread latte.',
        channel: 'Instagram',
        format: 'Carousel',
        status: 'Declined',
      },
    ],
  },
  {
    id: 'cmp-always',
    workspaceId: 'brzoza',
    name: 'Always-on',
    key: 'Always-on',
    goal: 'always-on',
    status: 'Active',
    channels: ['Instagram', 'Facebook', 'LinkedIn'],
    perWeek: 4,
    brief:
      'Everyday life at Brzoza: the team, the beans, the regulars. One LinkedIn post a week about the roastery.',
    materials: ['Brzoza beans', 'Cafe at dusk'],
    createdBy: 'Anna Nowak',
    ideas: [],
  },
  {
    id: 'cmp-holiday',
    workspaceId: 'brzoza',
    name: 'Holiday opening hours',
    key: 'Holiday opening hours',
    goal: 'event',
    status: 'Needs your input',
    start: 23,
    end: 30,
    channels: ['Instagram', 'Facebook'],
    perWeek: 5,
    brief: 'Tell guests early about opening hours over Christmas and New Year.',
    materials: [],
    createdBy: 'Marek Zieliński',
    ideas: [],
    owner: 'Marek Zieliński',
    template: 'tpl-announce',
    languages: ['pl'],
    accounts: ['brz-ig', 'brz-fb'],
    question: 'Which days will you be closed or open shorter between 24 December and 1 January?',
  },
  {
    id: 'cmp-autumn',
    workspaceId: 'lipa',
    name: 'Autumn at Lipa',
    key: 'Autumn at Lipa',
    goal: 'season',
    status: 'Active',
    start: 10,
    end: 30,
    channels: ['Instagram', 'Facebook'],
    perWeek: 3,
    brief: 'Linden tea, cinnamon buns and the window tables. Calm, homely, Polish first.',
    materials: [],
    createdBy: 'Anna Nowak',
    ideas: [],
  },
  {
    id: 'cmp-every',
    workspaceId: 'lipa',
    name: 'Every day',
    key: 'Every day',
    goal: 'always-on',
    status: 'Active',
    channels: ['Instagram', 'Facebook'],
    perWeek: 3,
    brief: 'Breakfast, pierogi and the people who make them.',
    materials: [],
    createdBy: 'Anna Nowak',
    ideas: [],
  },
  {
    id: 'cmp-events',
    workspaceId: 'lipa',
    name: 'Events',
    key: 'Events',
    goal: 'event',
    status: 'Active',
    start: 20,
    end: 20,
    channels: ['Instagram', 'Facebook'],
    perWeek: 2,
    brief: 'Friday jazz evenings.',
    materials: [],
    createdBy: 'Anna Nowak',
    ideas: [],
  },
];

export const seedTasks: Task[] = [
  {
    id: 't-holiday',
    workspaceId: 'brzoza',
    title: 'Plan the holiday opening hours posts',
    status: 'Waiting',
    kind: 'plan',
    by: 'Marek Zieliński',
    at: 'Yesterday',
    campaignId: 'cmp-holiday',
    question: 'Which days will you be closed or open shorter between 24 December and 1 January?',
    interpretation: {
      text: 'Posts on Instagram and Facebook announcing the holiday opening hours.',
    },
    nextStep: 'Answer the question so AMY can write the posts.',
  },
  {
    id: 't-buns',
    workspaceId: 'brzoza',
    title: 'Add a cinnamon buns post to the winter menu',
    status: 'Queued',
    kind: 'post',
    by: 'Marek Zieliński',
    at: 'Yesterday',
    conversationId: 'brz-c2',
    campaignId: 'cmp-winter',
    plannedFor: 27,
  },
  {
    id: 't-latte',
    workspaceId: 'brzoza',
    title: 'Make a TikTok from the latte art clips',
    status: 'Failed',
    kind: 'post',
    by: 'Anna Nowak',
    at: '08:20',
    conversationId: 'brz-c1',
    campaignId: 'cmp-winter',
    reason: 'TikTok needs reconnecting, so AMY could not prepare a TikTok version.',
  },
  {
    id: 't-ging',
    workspaceId: 'brzoza',
    title: 'Can you make a post about our new gingerbread latte for Thursday morning?',
    showAsWritten: true,
    status: 'Ready',
    kind: 'post',
    by: 'Anna Nowak',
    at: '09:15',
    conversationId: 'brz-c1',
    campaignId: 'cmp-winter',
    result: {
      text: 'Made {n} versions for {day}.',
      variables: { n: '3', day: '19 Nov' },
      postIds: ['brz-10', 'brz-11', 'brz-12'],
    },
  },
];

export type ColourRole =
  'Primary' | 'Secondary' | 'Background' | 'Text' | 'Accent' | 'Light accent' | 'Custom';
export interface BrandColour {
  name: string;
  role: ColourRole;
  hex: string;
  note?: string;
}
export interface BrandFont {
  role: 'Headings' | 'Body text' | 'Accent';
  family: string;
  category: string;
}
export interface BrandAsset {
  id: string;
  label: string;
  kind: 'Logo' | 'Icon' | 'Document' | 'Photo';
  src?: string;
  source: 'Drive' | 'Uploaded' | 'Material bank';
  file: string;
  bg: 'light' | 'dark' | 'any';
  note?: string;
}
export interface BrandSettings {
  oneLine: string;
  positioning: string;
  audience: string;
  products: string;
  facts: string;
  assets: BrandAsset[];
  colours: BrandColour[];
  pairs: [string, string, string][];
  fonts: BrandFont[];
  tones: string[];
  emoji: string;
  hashtags: string;
  cta: string;
  words: { use: string[]; avoid: string[] };
  languages: { code: string; title: string; note: string }[];
  notes: string;
  photoStyle: string[];
  logoRules: string[];
  examples: {
    name: string;
    image: string;
    why: string;
    by: string;
    day: number;
    caption?: string;
    postId?: string;
  }[];
  avoid: { text: string; why: string; rule: boolean }[];
}
export interface Brand {
  workspaceId: WorkspaceId;
  version: number;
  active: BrandSettings;
  draft: BrandSettings | null;
  draftBy?: string;
  history: {
    version: number;
    by: string;
    day: number;
    summary: string;
    parts?: { key: string; variables?: Record<string, string> }[];
    snapshot?: BrandSettings;
  }[];
}

const svg = (svgBody: string, width: number, height: number) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">${svgBody}</svg>`)}`;
const birchLeaf = (fill: string, vein: string) =>
  `<path d="M32 8c10 8 14 20 10 31-3 8-9 13-10 17-1-4-7-9-10-17-4-11 0-23 10-31z" fill="${fill}"/><path d="M32 18v36" stroke="${vein}" stroke-width="2" stroke-linecap="round"/>`;
const brzozaMark = (ink: string, leaf: string, vein: string) =>
  svg(
    `<g transform="translate(0 4)">${birchLeaf(leaf, vein)}</g><text x="72" y="44" font-family="'DM Serif Display', Georgia, serif" font-size="40" fill="${ink}">Brzoza</text><text x="74" y="64" font-family="Inter, Arial, sans-serif" font-size="11" letter-spacing="3.2" fill="${ink}">COFFEE · WARSZAWA</text>`,
    270,
    72
  );
const lindenLeaf = (fill: string) =>
  `<path d="M32 56C16 46 10 34 14 24c3-8 12-11 18-5 6-6 15-3 18 5 4 10-2 22-18 32z" fill="${fill}"/>`;
const lipaMark = (ink: string, leaf: string) =>
  svg(
    `<g transform="translate(0 6)">${lindenLeaf(leaf)}</g><text x="70" y="46" font-family="Fraunces, Georgia, serif" font-size="42" font-weight="600" fill="${ink}">Lipa</text><text x="72" y="64" font-family="'Work Sans', Arial, sans-serif" font-size="11" letter-spacing="3" fill="${ink}">KAWIARNIA · KRAKÓW</text>`,
    250,
    72
  );

const brzozaBrand: BrandSettings = {
  oneLine: 'Warm, natural and quietly confident. A neighbourhood cafe with room to slow down.',
  positioning: 'A neighbourhood cafe in Warsaw that roasts its own beans.',
  audience: 'Locals, remote workers and weekend regulars, mostly 25–45.',
  products: 'Seasonal lattes, single-origin filter coffee, homemade cakes.',
  facts: 'ul. Brzozowa 12, Warszawa · Beans roasted in Warsaw · Oat milk at no extra cost',
  assets: [
    {
      id: 'main',
      label: 'Main logo',
      kind: 'Logo',
      src: brzozaMark('#26312D', '#365A48', '#F2EBDD'),
      source: 'Drive',
      file: 'brzoza-logo-main.svg',
      bg: 'light',
    },
    {
      id: 'dark',
      label: 'For dark backgrounds',
      kind: 'Logo',
      src: brzozaMark('#F2EBDD', '#F2EBDD', '#365A48'),
      source: 'Drive',
      file: 'brzoza-logo-light.svg',
      bg: 'dark',
    },
    {
      id: 'icon',
      label: 'Icon',
      kind: 'Icon',
      src: svg(
        `<circle cx="32" cy="32" r="30" fill="#365A48"/>${birchLeaf('#F2EBDD', '#365A48')}`,
        64,
        64
      ),
      source: 'Drive',
      file: 'brzoza-icon.svg',
      bg: 'any',
      note: 'Profile pictures',
    },
    {
      id: 'menu',
      label: 'Winter menu',
      kind: 'Document',
      source: 'Uploaded',
      file: 'menu-zima-2026.pdf',
      bg: 'any',
      note: 'PDF · 2 pages',
    },
    {
      id: 'store',
      label: 'Storefront',
      kind: 'Photo',
      src: demoImages.dusk,
      source: 'Drive',
      file: 'storefront.jpg',
      bg: 'any',
    },
  ],
  colours: [
    { name: 'Forest', role: 'Primary', hex: '#365A48' },
    { name: 'Cream', role: 'Background', hex: '#F2EBDD' },
    { name: 'Ink', role: 'Text', hex: '#26312D' },
    { name: 'Cinnamon', role: 'Accent', hex: '#B5653A' },
    { name: 'Birch bark', role: 'Light accent', hex: '#D9D2C3' },
  ],
  pairs: [
    ['Ink', 'Cream', 'Captions and body text'],
    ['Forest', 'Cream', 'Headlines on cream'],
    ['Cream', 'Forest', 'Text on green banners'],
    ['Cream', 'Cinnamon', 'Offer badges'],
    ['Cinnamon', 'Cream', 'Small accent text'],
  ],
  fonts: [
    { role: 'Headings', family: 'DM Serif Display', category: 'Serif' },
    { role: 'Body text', family: 'Inter', category: 'Sans serif' },
  ],
  tones: ['warm', 'friendly'],
  emoji: 'few',
  hashtags: 'branded',
  cta: 'invite',
  words: {
    use: ['cosy', 'slow mornings', 'neighbourhood', 'freshly roasted'],
    avoid: ['cheap', 'best in Warsaw', 'limited time only'],
  },
  languages: [
    {
      code: 'PL',
      title: 'Polish · main language',
      note: 'Informal "Ty". Use Polish quotation marks „…”.',
    },
    {
      code: 'EN',
      title: 'English · LinkedIn and tourists',
      note: 'British spelling. Keep Polish place names.',
    },
  ],
  notes:
    'Always mention that we roast our beans in Warsaw. Polish captions use informal "Ty". Never promise prices in posts; link to the menu instead.',
  photoStyle: [
    'Natural daylight, warm tones',
    'Real guests and staff, never stock photos',
    'Cups and cakes close up, wooden tables',
  ],
  logoRules: [
    'Keep space around the logo the size of the leaf',
    'Use the light logo on photos and dark colours',
    'Never stretch, recolour or add effects',
  ],
  examples: [
    {
      name: 'Slow mornings, good coffee · Instagram',
      image: demoImages.cafe,
      why: 'Warm opening line, one clear invitation, one emoji.',
      by: 'Anna Nowak',
      day: 16,
      caption:
        'Slow mornings, good coffee. Our window seats are waiting for you until noon ☕ See you on Mokotowska?',
    },
    {
      name: 'A little winter ritual · Facebook',
      image: demoImages.cake,
      why: 'Shows real cake and the window seats.',
      by: 'Anna Nowak',
      day: 17,
      caption:
        'A little winter ritual: apple cake, a warm cup and the best seat by the window. Who are you bringing this week?',
    },
    {
      name: 'Meet the roaster · LinkedIn',
      image: demoImages.beans,
      why: 'Professional, but still personal.',
      by: 'Anna Nowak',
      day: 16,
      caption:
        'Every Tuesday our roaster Piotr roasts the beans you drink all week. Small batches, roasted in Warsaw, traced to the farm.',
    },
  ],
  avoid: [
    { text: '"Best coffee in Warsaw!!!"', why: 'Superlatives we cannot prove.', rule: true },
    { text: 'Stock photos of latte art', why: 'Use our own photos instead.', rule: true },
    {
      text: 'Rejected 6 Nov: "too formal" (Meet the roaster v1)',
      why: 'One-off feedback, not a rule.',
      rule: false,
    },
  ],
};

const lipaBrand: BrandSettings = {
  oneLine: 'Homely, generous and a little old-fashioned, in the best way.',
  positioning: 'A family cafe on Kazimierz serving breakfast, pierogi and linden tea.',
  audience: 'Kraków locals, families and visitors exploring Kazimierz.',
  products: 'All-day breakfast, homemade pierogi, linden tea with honey, cinnamon buns.',
  facts: 'ul. Józefa 8, Kraków · Family-run since 1998 · Live jazz every Friday',
  assets: [
    {
      id: 'main',
      label: 'Main logo',
      kind: 'Logo',
      src: lipaMark('#2B2A26', '#6E8B3D'),
      source: 'Uploaded',
      file: 'lipa-logo.svg',
      bg: 'light',
    },
    {
      id: 'dark',
      label: 'For dark backgrounds',
      kind: 'Logo',
      src: lipaMark('#FAF6EA', '#C9D98F'),
      source: 'Uploaded',
      file: 'lipa-logo-light.svg',
      bg: 'dark',
    },
    {
      id: 'icon',
      label: 'Icon',
      kind: 'Icon',
      src: svg(
        `<circle cx="32" cy="32" r="30" fill="#6E8B3D"/><g transform="translate(0 -2)">${lindenLeaf('#FAF6EA')}</g>`,
        64,
        64
      ),
      source: 'Uploaded',
      file: 'lipa-icon.svg',
      bg: 'any',
      note: 'Profile pictures',
    },
  ],
  colours: [
    { name: 'Linden', role: 'Primary', hex: '#6E8B3D' },
    { name: 'Milk', role: 'Background', hex: '#FAF6EA' },
    { name: 'Espresso', role: 'Text', hex: '#2B2A26' },
    { name: 'Honey', role: 'Accent', hex: '#E3B23C' },
  ],
  pairs: [
    ['Espresso', 'Milk', 'Captions and body text'],
    ['Linden', 'Milk', 'Headlines'],
    ['Espresso', 'Honey', 'Event badges'],
    ['Milk', 'Linden', 'Text on green'],
  ],
  fonts: [
    { role: 'Headings', family: 'Fraunces', category: 'Serif' },
    { role: 'Body text', family: 'Work Sans', category: 'Sans serif' },
  ],
  tones: ['warm'],
  emoji: 'few',
  hashtags: 'branded',
  cta: 'invite',
  words: { use: ['homemade', 'family', 'Kazimierz'], avoid: ['fast food', 'cheap'] },
  languages: [
    { code: 'PL', title: 'Polish · main language', note: 'Warm and polite. Use "Was" for groups.' },
  ],
  notes:
    'Mention the Friday jazz evening at least once a week. Never post photos of guests without asking.',
  photoStyle: ['Warm evening light', 'Food on old wooden tables', 'Family and staff, smiling'],
  logoRules: ['Use the green leaf on light backgrounds only'],
  examples: [
    {
      name: 'Breakfast until noon · Instagram',
      image: demoImages.bread,
      why: 'Simple, appetising, clear time.',
      by: 'Anna Nowak',
      day: 16,
      caption: 'Śniadania do 12:00 – świeże pieczywo, jajka i dobra kawa. Do zobaczenia!',
    },
  ],
  avoid: [{ text: 'Discount codes', why: 'Lipa does not run discounts.', rule: true }],
};

export const newClientBrand: BrandSettings = {
  ...brzozaBrand,
  oneLine: '',
  positioning: '',
  audience: '',
  products: '',
  facts: '',
  assets: [],
  colours: [],
  pairs: [],
  fonts: [
    { role: 'Headings', family: 'Inter', category: 'Sans serif' },
    { role: 'Body text', family: 'Inter', category: 'Sans serif' },
  ],
  tones: [],
  words: { use: [], avoid: [] },
  languages: [],
  notes: '',
  photoStyle: [],
  logoRules: [],
  examples: [],
  avoid: [],
};
export const seedBrands: Brand[] = [
  {
    workspaceId: 'brzoza',
    version: 1,
    active: brzozaBrand,
    draft: null,
    history: [
      { version: 1, by: 'Anna Nowak', day: 3, summary: 'First brand guide', snapshot: brzozaBrand },
    ],
  },
  {
    workspaceId: 'lipa',
    version: 1,
    active: lipaBrand,
    draft: null,
    history: [
      { version: 1, by: 'Anna Nowak', day: 5, summary: 'First brand guide', snapshot: lipaBrand },
    ],
  },
  { workspaceId: 'nowa', version: 0, active: newClientBrand, draft: null, history: [] },
];

export type NoticeChannel = 'app' | 'email' | 'push';
export type NoticeEvent = 'review' | 'changes' | 'published' | 'failed' | 'connection' | 'weekly';
export interface WorkspaceSettings {
  workspaceId: WorkspaceId;
  paused: boolean;
  pausedBy?: string;
  contributorCanApprove: boolean;
  contributorCanStop: boolean;
  notify?: Record<NoticeEvent, NoticeChannel[]>;
  quietHours?: boolean;
  contributorCanEditBrand?: boolean;
  postingTimezone?: string;
  displayTimezone?: string;
  dailyDigest?: boolean;
  personal?: OwnPreferencesByPersonOverridingWorkspaceDefaults;
}
type OwnPreferencesByPersonOverridingWorkspaceDefaults = Record<string, PersonalPreferences>;
export interface PersonalPreferences {
  notify: Record<NoticeEvent, NoticeChannel[]>;
  quietHours: boolean;
  displayTimezone?: string;
  dailyDigest?: boolean;
}
export function preferencesFor(
  workspaceSettings: WorkspaceSettings,
  person: string
): PersonalPreferences {
  return (
    workspaceSettings.personal?.[person] ?? {
      notify: workspaceSettings.notify ?? {
        review: [],
        changes: [],
        published: [],
        failed: ['app'],
        connection: [],
        weekly: [],
      },
      quietHours: !!workspaceSettings.quietHours,
    }
  );
}
export interface Member {
  id: string;
  workspaceId: WorkspaceId;
  name: string;
  email: string;
  role: 'Owner' | 'Contributor';
  status: 'Active' | 'Invited' | 'Expired';
  invitedAt?: string;
  invitedBy?: string;
  canApprove?: boolean;
  canStop?: boolean;
  canEditBrand?: boolean;
}
export const seedMembers: Member[] = [
  {
    id: 'mb-anna-b',
    workspaceId: 'brzoza',
    name: 'Anna Nowak',
    email: 'anna@brzoza.example',
    role: 'Owner',
    status: 'Active',
  },
  {
    id: 'mb-marek',
    workspaceId: 'brzoza',
    name: 'Marek Zieliński',
    email: 'marek@brzoza.example',
    role: 'Contributor',
    status: 'Active',
  },
  {
    id: 'mb-ola-inv',
    workspaceId: 'brzoza',
    name: 'Ola Wójcik',
    email: 'ola@brzoza.example',
    role: 'Contributor',
    status: 'Expired',
    invitedAt: '9 Nov',
    invitedBy: 'Anna Nowak',
  },
  {
    id: 'mb-anna-l',
    workspaceId: 'lipa',
    name: 'Anna Nowak',
    email: 'anna@lipa.example',
    role: 'Owner',
    status: 'Active',
  },
  {
    id: 'mb-zofia',
    workspaceId: 'lipa',
    name: 'Zofia Mazur',
    email: 'zofia@lipa.example',
    role: 'Contributor',
    status: 'Active',
  },
  {
    id: 'mb-julia',
    workspaceId: 'nowa',
    name: 'Julia Wiśniewska',
    email: 'julia@example.com',
    role: 'Owner',
    status: 'Active',
  },
];
export interface StaffMember {
  id: string;
  name: string;
  role: string;
  email: string;
  workspaces: WorkspaceId[];
  lead?: WorkspaceId[];
}
export const seedStaff: StaffMember[] = [
  {
    id: 'st-kasia',
    name: 'Kasia Lewandowska',
    role: 'Account manager',
    email: 'kasia@hakusana.example',
    workspaces: ['brzoza', 'lipa'],
    lead: ['brzoza'],
  },
  {
    id: 'st-tomek',
    name: 'Tomek Kamiński',
    role: 'Content specialist',
    email: 'tomek@hakusana.example',
    workspaces: ['brzoza'],
    lead: ['lipa'],
  },
  {
    id: 'st-ewa',
    name: 'Ewa Dąbrowska',
    role: 'Designer',
    email: 'ewa@hakusana.example',
    workspaces: [],
  },
];
export const seedSettings: WorkspaceSettings[] = [
  {
    workspaceId: 'brzoza',
    paused: false,
    contributorCanApprove: false,
    contributorCanStop: false,
    quietHours: true,
    notify: {
      review: ['app', 'email'],
      changes: ['app'],
      published: ['app'],
      failed: ['app', 'email', 'push'],
      connection: ['app', 'email'],
      weekly: ['email'],
    },
  },
  {
    workspaceId: 'lipa',
    paused: false,
    contributorCanApprove: false,
    contributorCanStop: false,
    quietHours: true,
    notify: {
      review: ['app', 'email'],
      changes: ['app'],
      published: ['app'],
      failed: ['app', 'email', 'push'],
      connection: ['app', 'email'],
      weekly: ['email'],
    },
  },
  {
    workspaceId: 'nowa',
    paused: false,
    contributorCanApprove: false,
    contributorCanStop: false,
    quietHours: true,
    notify: {
      review: ['app', 'email'],
      changes: ['app'],
      published: ['app'],
      failed: ['app', 'email', 'push'],
      connection: ['app', 'email'],
      weekly: ['email'],
    },
  },
];

export interface Notice {
  id: string;
  workspaceId: WorkspaceId;
  at: string;
  text: string;
  variables?: Record<string, string>;
  link: string;
  read: boolean;
}
export const seedNotices: Notice[] = [
  {
    id: 'n1',
    workspaceId: 'brzoza',
    at: '09:16',
    text: '{title} is ready for your review',
    variables: { title: 'Gingerbread latte is here' },
    link: 'post/brz-10',
    read: false,
  },
  {
    id: 'n2',
    workspaceId: 'brzoza',
    at: 'Yesterday',
    text: 'TikTok needs reconnecting',
    link: 'settings',
    read: false,
  },
  {
    id: 'n3',
    workspaceId: 'brzoza',
    at: 'Yesterday',
    text: '{title}: published on {ok} of {n} accounts',
    variables: { title: 'Our roastery in Warsaw', ok: '1', n: '2' },
    link: 'post/brz-6',
    read: true,
  },
  {
    id: 'n4',
    workspaceId: 'lipa',
    at: '08:55',
    text: '{title} is ready for your review',
    variables: { title: 'Pierogi Thursday' },
    link: 'post/lip-4',
    read: false,
  },
];

type WeekdayNumberWhereMondayIsOne = number;
export interface OpeningHours {
  day: WeekdayNumberWhereMondayIsOne;
  open: string;
  close: string;
  closed?: boolean;
}
export interface Business {
  workspaceId: WorkspaceId;
  name: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  menuUrl: string;
  hours: OpeningHours[];
  exceptions: { date: string; note: string }[];
  products: string;
  audience: string;
  updatedBy?: string;
  updatedAt?: string;
}
const week = (
  open: string,
  close: string,
  sat: [string, string],
  sun?: [string, string]
): OpeningHours[] =>
  [1, 2, 3, 4, 5]
    .map((day): OpeningHours => ({ day, open, close }))
    .concat([
      { day: 6, open: sat[0], close: sat[1] },
      sun ? { day: 7, open: sun[0], close: sun[1] } : { day: 7, open: '', close: '', closed: true },
    ]);
export const seedBusiness: Business[] = [
  {
    workspaceId: 'brzoza',
    name: 'Brzoza Coffee',
    address: 'ul. Brzozowa 12, 00-286 Warszawa',
    phone: '+48 22 100 20 30',
    email: 'hello@brzoza.example',
    website: 'https://brzoza.example',
    menuUrl: 'https://brzoza.example/menu',
    hours: week('07:30', '19:00', ['09:00', '19:00'], ['10:00', '17:00']),
    exceptions: [{ date: '24 Dec', note: 'Closed 24–26 December' }],
    products: 'Speciality coffee roasted in Warsaw, gingerbread latte, apple cake, hot chocolate',
    audience: 'Neighbours and office workers nearby, 25–45',
    updatedBy: 'Anna Nowak',
    updatedAt: '3 Nov',
  },
  {
    workspaceId: 'lipa',
    name: 'Kawiarnia Lipa',
    address: 'ul. Lipowa 4, 30-001 Kraków',
    phone: '+48 12 300 40 50',
    email: 'czesc@lipa.example',
    website: 'https://lipa.example',
    menuUrl: '',
    hours: week('08:00', '18:00', ['09:00', '16:00']),
    exceptions: [],
    products: 'Linden tea, cinnamon buns, breakfasts, pierogi',
    audience: 'Locals and tourists in the old town',
    updatedBy: 'Anna Nowak',
    updatedAt: '5 Nov',
  },
  {
    workspaceId: 'nowa',
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    menuUrl: '',
    hours: week('09:00', '17:00', ['10:00', '14:00']),
    exceptions: [],
    products: '',
    audience: '',
  },
];
export type AiScenario = 'success' | 'invalid' | 'billing' | 'model' | 'temporary';
export interface AiConnection {
  workspaceId: WorkspaceId;
  status: 'none' | 'connected' | 'error';
  provider?: string;
  model?: string;
  keyEnding?: string;
  lastTest?: string;
  connectedBy?: string;
  error?: AiScenario;
  trial?: DraftsOnAgencyTrialAllowanceUntilClientConnectsOwnAi;
  history?: EarlierModelsKeptByTheirUsageRows;
}
type DraftsOnAgencyTrialAllowanceUntilClientConnectsOwnAi = boolean;
type EarlierModelsKeptByTheirUsageRows = { provider: string; model: string; keys: string[] }[];
export const aiWorks = (connection?: AiConnection) =>
  !!connection && (connection.status === 'connected' || !!connection.trial);
export const seedAi: AiConnection[] = [
  { workspaceId: 'brzoza', status: 'none', trial: true },
  {
    workspaceId: 'lipa',
    status: 'connected',
    provider: 'openai',
    model: 'Mid-size model (example)',
    keyEnding: '7Q2k',
    lastTest: '5 Nov, 10:12',
    connectedBy: 'Anna Nowak',
  },
  { workspaceId: 'nowa', status: 'none', trial: true },
];
export interface HelperInvite {
  id: string;
  workspaceId: WorkspaceId;
  name: string;
  email: string;
  scope: 'ai';
  status: 'Pending' | 'Completed' | 'Withdrawn';
  sentAt: string;
  completedAt?: string;
  by: string;
}
export type SetupStep =
  'business' | 'brand' | 'materials' | 'ai' | 'social' | 'samples' | 'activate';
export interface Onboarding {
  workspaceId: WorkspaceId;
  later: SetupStep[];
  samples: string[];
  activated?: string;
}
export const seedOnboarding: Onboarding[] = [
  { workspaceId: 'brzoza', later: [], samples: ['brz-13', 'brz-15'] },
  { workspaceId: 'lipa', later: [], samples: [], activated: '5 Nov' },
  { workspaceId: 'nowa', later: [], samples: [] },
];

export interface AgencyRequest {
  id: string;
  workspaceId: WorkspaceId;
  from: string;
  text: string;
  at: string;
  status: 'Open' | 'Answered';
  reply?: { by: string; text: string; at: string };
}
